/**
 * Browser multipart upload (design doc §13.3): the API hands out presigned part URLs, the browser PUTs each slice
 * straight to storage, then the API completes the upload. This module only orchestrates; every side effect comes
 * in through `UploadPorts`, so it runs the same against the real API, a test double, or another storage backend.
 */
import type { PartUrl, UploadedPart, UploadInit } from "../api/types";

export interface UploadPorts {
  init(file: { filename: string; size: number; contentType: string }): Promise<UploadInit>;
  partUrls(videoId: string, partNumbers: number[]): Promise<{ urls: PartUrl[]; expiresInSeconds: number }>;
  /** PUTs one slice and resolves with the ETag header. `onProgress` gets bytes sent so far for this part. */
  putPart(url: string, body: Blob, onProgress: (sent: number) => void, signal: AbortSignal): Promise<string>;
  complete(videoId: string, parts: UploadedPart[]): Promise<unknown>;
  abort(videoId: string): Promise<unknown>;
}

export interface UploadOptions {
  contentType: string;
  /** Parts in flight at once. */
  concurrency?: number;
  /** Tries per part before the whole upload fails. */
  attempts?: number;
  onStarted?: (videoId: string) => void;
  onProgress?: (sent: number, total: number) => void;
  signal?: AbortSignal;
  /** Waits between retries; injectable so tests don't sleep. */
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
}

/** Most part numbers the API presigns per request. */
export const PRESIGN_BATCH = 100;

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export async function uploadMultipart(file: Blob & { name: string }, ports: UploadPorts, opts: UploadOptions): Promise<string> {
  const { concurrency = 4, attempts = 4, sleep = defaultSleep, now = Date.now } = opts;
  const signal = opts.signal ?? new AbortController().signal;
  signal.throwIfAborted();

  const { videoId, partSize, partCount } = await ports.init({ filename: file.name, size: file.size, contentType: opts.contentType });
  opts.onStarted?.(videoId);

  const sentByPart = new Map<number, number>();
  const report = () => opts.onProgress?.([...sentByPart.values()].reduce((a, b) => a + b, 0), file.size);

  // Presigned URLs are fetched lazily in batches and refreshed shortly before they expire.
  const batches = new Map<number, Promise<{ urls: Map<number, string>; expiresAt: number }>>();
  const urlFor = async (part: number, fresh: boolean): Promise<string> => {
    const b = Math.floor((part - 1) / PRESIGN_BATCH);
    let pending = batches.get(b);
    const cached = pending && (await pending.catch(() => null));
    if (fresh || !cached || cached.expiresAt <= now()) {
      const first = b * PRESIGN_BATCH + 1;
      const nums = Array.from({ length: Math.min(PRESIGN_BATCH, partCount - first + 1) }, (_, i) => first + i);
      pending = ports.partUrls(videoId, nums).then((r) => ({
        urls: new Map(r.urls.map((u) => [u.partNumber, u.url])),
        expiresAt: now() + r.expiresInSeconds * 800, // refresh at 80% of the lifetime
      }));
      batches.set(b, pending);
    }
    const url = (await pending!).urls.get(part);
    if (!url) throw new Error(`No upload URL for part ${part}`);
    return url;
  };

  const uploadPart = async (part: number): Promise<UploadedPart> => {
    const slice = file.slice((part - 1) * partSize, Math.min(part * partSize, file.size));
    for (let attempt = 1; ; attempt++) {
      signal.throwIfAborted();
      try {
        const url = await urlFor(part, attempt > 1);
        const etag = await ports.putPart(url, slice, (sent) => { sentByPart.set(part, sent); report(); }, signal);
        sentByPart.set(part, slice.size);
        report();
        return { partNumber: part, etag };
      } catch (e) {
        sentByPart.set(part, 0);
        report();
        if (signal.aborted || attempt >= attempts) throw e;
        await sleep(500 * 2 ** (attempt - 1));
      }
    }
  };

  try {
    const parts: UploadedPart[] = [];
    let next = 1;
    const worker = async () => {
      while (next <= partCount) {
        const part = next++;
        parts.push(await uploadPart(part));
      }
    };
    await Promise.all(Array.from({ length: Math.min(concurrency, partCount) }, worker));
    parts.sort((a, b) => a.partNumber - b.partNumber);
    await ports.complete(videoId, parts);
    return videoId;
  } catch (e) {
    await ports.abort(videoId).catch(() => undefined); // best effort; the API also expires stale uploads
    throw e;
  }
}
